import React, { useState, useEffect, useContext } from 'react';
import './style.css';
import RiskOverviewStandardSectionHeader from '/src/Components/RiskOverviewStandardSectionHeader/RiskOverviewStandardSectionHeader';
// import RiskOverviewSectionHeader from '../RiskOverviewSectionHeader/RiskOverviewSectionHeader';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import CurrentBillsEstimate from './CurrentBillsEstimate/CurrentBillsEstimate';
import FourLinePlusBarChart from '/src/Components/FourLinePlusBarChart/FourLinePlusBarChart';
import SeeMoreExpensesData from './SeeMoreExpensesData/SeeMoreExpensesData';
import RiskOverviewSectionLoadingState from '/src/Components/RiskOverviewSectionLoadingState/RiskOverviewSectionLoadingState';
import { AreaDataContext } from '../../PropertyExplorer';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import HowWorriedShouldIBe from '/src/Components/HowWorriedShouldIBe/HowWorriedShouldIBe';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';

// Define colors as constants
const EXPENSE_COLORS = {
    insurance: '#00A7B3',    // teal
    maintenance: '#9E9E9E',  // grey
    utilities: '#FFD700',    // yellow
    property_taxes: '#FF69B4' // pink
};

const RecurringBills = ({ propertyData, title }) => {
    const { deviceType } = useContext(WindowSizeContext);
    const { userPersonalInfo, quollScoreAnalysis } = useContext(AreaDataContext);
    // const { LOG_GA_EVENT } = useContext(AddressSearchContext);
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();
    // Set the status for the should I be worried sentence and meter level
    const [shouldIBeWorriedSentence, setShouldIBeWorriedSentence] = useState(null);
    const [meterLevel, setMeterLevel] = useState(0);

    const intro_sentence = `Now that we have a clear picture of the climate risks, let’s look at how these risks will impact your recurring bills. Climate risk doesn’t just bring big one-time costs — it can quietly push up your monthly bills. From insurance and maintenance to utilities and taxes, here’s how climate exposure may already be affecting your expenses.`

    const [seeMore, setSeeMore] = useState(false);
    // Define expense types with their colors
    const expenses = [
        { id: 'insurance', label: 'Insurance', color: EXPENSE_COLORS.insurance },
        { id: 'maintenance', label: 'Maintenance', color: EXPENSE_COLORS.maintenance },
        { id: 'utilities', label: 'Utilities', color: EXPENSE_COLORS.utilities },
        { id: 'property_taxes', label: 'Property Taxes', color: EXPENSE_COLORS.property_taxes }
    ];

    // Get data for each expense type
    const getExpenseData = (expenseId) => {
        const currentValue = propertyData?.data?.current_opex?.[expenseId] || 0;
        const dataKey = expenseId === 'property_taxes' ? 'taxes' : expenseId;

        const projectedValues = propertyData?.data?.scenario_modeling?.individual_breakdowns?.[0]
            ?.financials_no_improvements_made?.breakdown?.opex?.annual_by_expense_type?.[dataKey]
            ?.map(item => item.value) || [];

        return [currentValue, ...projectedValues];
    };

    // Generate labels for the timeline
    const labels = ['Today', ...Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`)];

    // Collect all expense data
    const expenseData = expenses.reduce((acc, expense) => {
        acc[expense.id] = getExpenseData(expense.id);
        return acc;
    }, {});

    // Calculate total expenses for each time period
    const totalExpenses = labels.map((_, index) => {
        return expenses.reduce((sum, expense) => {
            const expenseValues = expenseData[expense.id];
            return sum + (expenseValues[index] || 0);
        }, 0);
    });

    // Format line data for the chart
    const lineData = expenses.map(expense => ({
        label: expense.label,
        data: expenseData[expense.id],
        color: expense.color,
        shape: 'circle'
    }));

    // Create a new object that takes the lineData and add a total object at the beginning
    const mobileLegendItems = [
        {
            label: 'Total',
            data: totalExpenses,
            color: 'rgba(198, 198, 198, 0.12)',
            shape: 'square'
        },
        ...lineData
    ];

    const recurring_bills_intro_sentence = `We’ve talked about this home’s expenses as the homeowner, but now we need to put ourselves in a potential buyer’s shoes. Remember, they will need to be able to afford these expenses plus whatever mortgage costs they take on. `

    // useEffect that listens to userPersonalInfo quollScoreAnalysis and sets the shouldIBeWorriedSentence
    useEffect(() => {

        if (userPersonalInfo && userPersonalInfo.isComplete && quollScoreAnalysis) {

            // Get the information from userPersonalInfo
            const userRelationToProperty = userPersonalInfo.relationToProperty || null;
            const userHouseholdIncome = userPersonalInfo.householdIncome || null;
            const userExpectedPurchasePrice = userPersonalInfo.expectedPurchasePrice || null;
            const userMonthlyMortgagePayment = userPersonalInfo.monthlyMortgagePayment || null;
            const availableFunds = userPersonalInfo.availableFunds || null;
            const availableFundsFormatted = availableFunds ? availableFunds.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : null;

            // Get the meter level from the quollScoreAnalysis
            const meterLevel = quollScoreAnalysis.opex.opexRiskLevel;

            // console.log('WORRIED INPUTS', meterLevel, availableFundsFormatted);

            // Create the sentence starter based on the userRelationToProperty
            let sentenceStarter = null;
            if (userRelationToProperty === 'owner') {
                sentenceStarter = `As the owner of this home, `;
            } else {
                sentenceStarter = `Were you to purchase this home, `;
            }

            let sentence = null;
            // Get the sentence from the quollScoreAnalysis
            if (meterLevel && availableFundsFormatted) {
                if (meterLevel <= 3) {
                    sentence = `${sentenceStarter} you ${userRelationToProperty === 'owner' ? 'do not' : 'would not'} need to worry much about your ability to pay these recurring costs as you wouldn't be crossing into dangerous territory. That said, it is important to pay attention to any changes to these costs!`;
                } else if (meterLevel <= 5) {
                    sentence = `${sentenceStarter} you shouldn't be too worried about your ability to cover these costs, however, this is definitely something to pay attention to. You're on the upper edge of the range for how much of your household income is going to those bills, so acting to reduce them may be wise.`;
                } else if (meterLevel <= 7) {
                    sentence = `${sentenceStarter} you need to pay serious attention to these costs, as your ability to cover them could be at risk. They represent a significant portion of your household income so we recommend considering taking action to reduce them.`;
                } else {
                    sentence = `${sentenceStarter} you ${userRelationToProperty === 'owner' ? 'need to seriously consider your future in this home' : 'need to seriously consider your ability to afford this home'} because when we look at these ownership costs you'd be devoting a significant portion of your household income to them. Taking action to reduce these costs is highly recommended.`;
                }
            }

            setShouldIBeWorriedSentence(sentence);
            setMeterLevel(meterLevel);
        }
    }, [userPersonalInfo, quollScoreAnalysis]);

    // useEffect to log when seeMore is toggled to true
    useEffect(() => {
        if (seeMore) {
            handleLoggingGaEventFromProperty('see_data_driving_opex');
        }
    }, [seeMore]);

    return (
        <div className="tab-content-container">
            <RiskOverviewStandardSectionHeader title={title} />
            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: intro_sentence }} />
            <div className="climate-in-your-area-see-more-container">
                <div className={`recurring-bills ${deviceType}`}>
                    {/* Left side - Current expenses */}
                    <div className="recurring-bills__current-expenses">
                        <h3 className="section-subtitle">Our Estimate of Current Expenses</h3>
                        <CurrentBillsEstimate propertyData={propertyData} />
                    </div>

                    {/* <CurlyBraceDivider direction="right" color="grey" /> */}

                    {/* Right side - Projected expenses chart */}
                    <div className="recurring-bills__projected-expenses">
                        <h3 className="section-subtitle">Where Climate is Pushing Those Expenses</h3>
                        <div className="recurring-bills__chart-container">

                            <div className="recurring-bills__chart">
                                <FourLinePlusBarChart
                                    lineData={lineData}
                                    barData={totalExpenses}
                                    labels={labels}
                                    deviceType={deviceType}
                                    expenseColors={EXPENSE_COLORS}
                                />
                            </div>
                            {deviceType === 'mobile' && (
                                <div className="recurring-bills__chart-legend">
                                    {mobileLegendItems.map((line) => (
                                        <div className="recurring-bills__chart-legend-item" key={line.label}>
                                            <div className={`recurring-bills__chart-legend-item-color ${line.shape}`} style={{ backgroundColor: line.color }} />
                                            <span className="recurring-bills__chart-legend-item-label text-align-center">{line.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <button
                    className="climate-in-your-area-see-more-button"
                    onClick={() => seeMore ? setSeeMore(false) : setSeeMore(true)}
                >
                    {seeMore ? 'Hide Additional Data' : 'What data is driving this?'}
                </button>
                {seeMore && (
                    <>
                        {/* <CurlyBraceDivider direction="down" color="grey" /> */}
                        <SeeMoreExpensesData propertyData={propertyData} />
                    </>

                )}
            </div>
            <HowWorriedShouldIBe
                meterLevel={meterLevel}
                sentence={shouldIBeWorriedSentence}
                userPersonalInfo={userPersonalInfo}
                type="recurring-bills"
            />
        </div>
    );
};

export default RecurringBills;